import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { space as styledSpace, SpaceProps } from 'styled-system';

import { themeByVariant } from './themeByVariant';

export type ButtonVariant = 'filled' | 'outlined' | 'text';
export type ButtonColor = 'primary' | 'secondary' | 'error';
export type ButtonSize = 's' | 'm' | 'l';

const widthBySize: Record<ButtonSize, number> = {
  s: 90,
  m: 100,
  l: 120,
};

const sizeStyles: Record<
  ButtonSize,
  (props: { theme: import('../../theme/theme').Theme }) => ReturnType<typeof css>
> = {
  s: ({ theme }) => css`
    height: ${theme.space[3]}px;
    padding: ${theme.space[0]}px 9px;
    font-size: ${theme.fontSizes[2]}px;
    min-width: ${widthBySize.s}px;
  `,
  m: ({ theme }) => css`
    height: ${theme.space[4]}px;
    padding: ${theme.space[1]}px 10px;
    font-size: ${theme.fontSizes[2]}px;
    min-width: ${widthBySize.m}px;
  `,
  l: ({ theme }) => css`
    height: ${theme.space[5]}px;
    padding: 13px ${theme.space[2]}px;
    font-size: ${theme.fontSizes[4]}px;
    min-width: ${widthBySize.l}px;
  `,
};

interface ButtonWrapperProps extends SpaceProps {
  $variant: ButtonVariant;
  $color: ButtonColor;
  $size: ButtonSize;
  $active: boolean;
  $loading: boolean;
  $disabled: boolean;
}

const ButtonWrapper = styled.button<ButtonWrapperProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $loading, $disabled }) =>
    $loading || $disabled ? 'default' : 'pointer'};
  white-space: nowrap;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-align: center;
  font-family: inherit;
  transition:
    0.2s ease-in-out background,
    0.2s ease-in-out color,
    0.2s ease-in-out border;

  ${({ $variant, $color }) => {
    const v = themeByVariant[$variant][$color];
    return css`
      ${v.default}
      &:hover:not(:disabled) {
        ${v.hover}
      }
    `;
  }}

  &:disabled {
    ${({ $variant, $color, $loading }) => {
      const v = themeByVariant[$variant][$color];
      return $loading ? v.default : v.disabled;
    }}
  }

  ${({ $size }) => sizeStyles[$size]}

  ${({ $variant }) =>
    $variant === 'text' &&
    css`
      min-width: inherit;
    `}

  ${({ $size, theme }) =>
    ($size === 's' || $size === 'm') &&
    css`
      ${theme.device.mobile} {
        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          min-width: ${theme.layout.touchTargetMin}px;
          min-height: ${theme.layout.touchTargetMin}px;
        }
      }
    `}

  ${styledSpace}
`;

const ChildrenWrapper = styled.span<{ $loading: boolean }>`
  visibility: ${({ $loading }) => ($loading ? 'hidden' : 'visible')};
  display: flex;
  align-items: center;
`;

const Spinner = styled.span`
  position: absolute;
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export interface ButtonProps extends SpaceProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  color?: ButtonColor;
  disabled?: boolean;
  loading?: boolean;
  active?: boolean;
  type?: 'button' | 'submit' | 'reset';
  children: ReactNode;
  onClick?: () => void;
  /** Имя для вспомогательных технологий; видимый текст кнопки может оставаться коротким. */
  'aria-label'?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'filled',
      size = 'm',
      color = 'primary',
      disabled = false,
      loading = false,
      active = false,
      type = 'button',
      children,
      onClick,
      'aria-label': ariaLabel,
      ...spaceProps
    },
    ref,
  ) => {
    return (
      <ButtonWrapper
        ref={ref}
        type={type}
        $variant={variant}
        $color={color}
        $size={size}
        $active={active}
        $loading={loading}
        $disabled={disabled || loading}
        disabled={disabled || loading}
        onClick={onClick}
        aria-label={ariaLabel}
        {...spaceProps}
      >
        {loading && <Spinner aria-hidden />}
        <ChildrenWrapper $loading={loading}>{children}</ChildrenWrapper>
      </ButtonWrapper>
    );
  },
);

Button.displayName = 'Button';
